"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.getImagesByDimension = exports.getSrcsetFrom = exports.getSrcsetEntryFrom = exports.getImageUrl = exports.mediaQueries = exports.BreakpointsPx = exports.Fit = exports.DPR = exports.Quality = void 0;
const js_core_1 = __importDefault(require("@imgix/js-core"));
const cache_1 = require("./cache");
const HOST = process.env.IMGIX_HOST || 'example.imgix.net';
const TOKEN = HOST === 'example.imgix.net' ? '' : process.env.IMGIX_TOKEN || '';
const INCLUDE_IMGIX_VERSION = false;
const imgUrlReplacer = [`https://${HOST}`, '/images'];
var Quality;
(function (Quality) {
    Quality[Quality["HIGH"] = 75] = "HIGH";
    Quality[Quality["LOW"] = 50] = "LOW";
    Quality[Quality["XS"] = 35] = "XS";
})(Quality || (exports.Quality = Quality = {}));
var DPR;
(function (DPR) {
    DPR[DPR["LOW"] = 1] = "LOW";
    DPR[DPR["HIGH"] = 2] = "HIGH";
    DPR[DPR["ULTRAHIGH"] = 3] = "ULTRAHIGH";
})(DPR || (exports.DPR = DPR = {}));
var Fit;
(function (Fit) {
    Fit["clip"] = "clip";
    Fit["clamp"] = "clamp";
    Fit["crop"] = "crop";
    Fit["facearea"] = "facearea";
    Fit["fill"] = "fill";
    Fit["fillmax"] = "fillmax";
    Fit["max"] = "max";
    Fit["min"] = "min";
    Fit["scale"] = "scale";
})(Fit || (exports.Fit = Fit = {}));
const IMGIX_OPTIONS = {
    w: undefined,
    h: undefined,
    fit: Fit.clip,
    auto: 'format,compress',
    cs: 'srgb',
    dpr: 1,
    q: undefined,
};
var BreakpointsPx;
(function (BreakpointsPx) {
    BreakpointsPx[BreakpointsPx["xs"] = 480] = "xs";
    BreakpointsPx[BreakpointsPx["xl"] = 481] = "xl";
})(BreakpointsPx || (exports.BreakpointsPx = BreakpointsPx = {}));
const imgixClient = new js_core_1.default({
    domain: HOST,
    includeLibraryParam: INCLUDE_IMGIX_VERSION,
    secureURLToken: TOKEN,
});
exports.mediaQueries = [
    `(max-width:${BreakpointsPx.xs}px) and (-webkit-device-pixel-ratio:1)`,
    `(min-width:${BreakpointsPx.xl}px) and (-webkit-device-pixel-ratio:1)`,
    `(max-width:${BreakpointsPx.xs}px) and (-webkit-min-device-pixel-ratio:2)`,
    `(min-width:${BreakpointsPx.xl}px) and (-webkit-min-device-pixel-ratio:2)`,
];
const sizesQuery = `${exports.mediaQueries[0]} 1px,${exports.mediaQueries[1]} 2px,${exports.mediaQueries[2]} 3px,${exports.mediaQueries[3]} 16px`;
const getImageCacheKey = (height, width, fit, dpr, q, urlReplacer) => {
    return `${height}_${width}_${fit}_${dpr}_${q ?? ''}_${urlReplacer?.join('_') ?? ''}`;
};
function getImageUrl(sourceUrl, dimensions = {}, q = undefined, defaultFit = Fit.clip, dpr = DPR.LOW, urlReplacer = imgUrlReplacer) {
    const { height, width, fit } = dimensions;
    if (TOKEN === '')
        return sourceUrl;
    const cacheKey = getImageCacheKey(height ?? 0, width, fit ?? defaultFit, dpr, q, urlReplacer);
    let url = (0, cache_1.get)(sourceUrl, cacheKey);
    if (url !== undefined)
        return url;
    url = imgixClient.buildURL(sourceUrl, {
        ...IMGIX_OPTIONS,
        w: width,
        h: height,
        fit: fit ?? defaultFit,
        dpr,
        q,
    });
    if (urlReplacer !== null)
        url = url.replace(urlReplacer[0], urlReplacer[1]);
    (0, cache_1.set)(sourceUrl, cacheKey, url);
    return url;
}
exports.getImageUrl = getImageUrl;
function getSrcsetEntryFrom(url, imgDimensions, q, fit, width = imgDimensions.width, urlReplacer = imgUrlReplacer) {
    if (!imgDimensions)
        return { src: url, str: '' };
    const src = getImageUrl(url, imgDimensions, q, fit, q >= Quality.HIGH ? 1 : 2, urlReplacer);
    return {
        src,
        str: `${src} ${width}w,`,
    };
}
exports.getSrcsetEntryFrom = getSrcsetEntryFrom;
function getSrcsetFrom(url, imgDimensions, includePreload = false, urlReplacer = imgUrlReplacer) {
    const dimensionsXL = imgDimensions[BreakpointsPx.xl];
    const result1 = getSrcsetEntryFrom(url, imgDimensions[BreakpointsPx.xs], Quality.HIGH, Fit.clip, 1, urlReplacer);
    const result2 = getSrcsetEntryFrom(url, dimensionsXL, Quality.HIGH, Fit.clip, 2, urlReplacer);
    const result3 = getSrcsetEntryFrom(url, imgDimensions[BreakpointsPx.xs], Quality.XS, Fit.max, 15, urlReplacer);
    const result4 = getSrcsetEntryFrom(url, imgDimensions[BreakpointsPx.xl], Quality.LOW, Fit.max, 16, urlReplacer);
    const lowDPR = result1.str + result2.str;
    const highDPR = result3.str + result4.str;
    let preload;
    if (includePreload) {
        preload = {
            [exports.mediaQueries[0]]: result1.src,
            [exports.mediaQueries[1]]: result2.src,
            [exports.mediaQueries[2]]: result3.src,
            [exports.mediaQueries[3]]: result4.src,
        };
    }
    return {
        src: result4.src,
        str: lowDPR + highDPR,
        preload,
    };
}
exports.getSrcsetFrom = getSrcsetFrom;
function getImagesByDimension(imgUrl, imgDimensions, includePreload = false, urlReplacer = imgUrlReplacer) {
    const biggestImg = imgDimensions[BreakpointsPx.xl];
    const srcset = imgUrl
        ? getSrcsetFrom(imgUrl, imgDimensions, includePreload, urlReplacer)
        : { str: '', src: '', preload: undefined };
    return {
        originalSrc: imgUrl,
        src: srcset.src,
        width: biggestImg.width,
        height: biggestImg.height,
        srcset: srcset.str,
        sizes: sizesQuery,
        preload: srcset.preload,
    };
}
exports.getImagesByDimension = getImagesByDimension;
//# sourceMappingURL=imgix.js.map